import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Lock,
  MessageSquare,
  ShieldCheck,
  Zap,
} from "lucide-react";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import { Reveal } from "@/components/Reveal";
import { DuckBadge } from "@/components/DuckMascot";
import { submitProApplication } from "@/lib/api";

// Pro-facing page: recruits licensed Connecticut pest-control companies to buy
// leads. Submissions go to the Netlify "pro" form, which submission-created
// deliberately ignores, so an application is never routed as a homeowner lead.

const PEST_CHOICES = ["Mosquitoes", "Ticks", "Wasps/Hornets", "Ants", "Fleas", "Rodents", "General Pest"];

const CT_COUNTIES = [
  "Fairfield",
  "Hartford",
  "Litchfield",
  "Middlesex",
  "New Haven",
  "New London",
  "Tolland",
  "Windham",
];

const DELIVERY = ["Text message", "Email", "Webhook / CRM"];

// Every field below is collected by the homeowner request form (LeadForm.jsx).
const LEAD_FIELDS = [
  { k: "The pest", v: "Mosquitoes, ticks, wasps, ants, fleas, rodents, or general pest" },
  { k: "How soon", v: "From \"as soon as possible\" to \"just planning ahead\"" },
  { k: "Where", v: "Street address, town and ZIP" },
  { k: "Property type", v: "Single-family, condo, multi-family, commercial" },
  { k: "Who", v: "Name, phone and email" },
  { k: "Best way to reach them", v: "Call, text or email, their pick" },
  { k: "In their words", v: "A short note on what's going on" },
  { k: "Consent", v: "They've agreed to be contacted by a provider" },
];

const STEPS = [
  {
    icon: BookOpen,
    title: "They read first",
    body: "Homeowners find our Connecticut-only guides on ticks, mosquitoes, wasps, mice and more. By the time they ask for help, they know what they're dealing with.",
  },
  {
    icon: ClipboardList,
    title: "They ask for a pro",
    body: "A three-step request: the problem, the property, the person. It takes about a minute and nothing is optional that you'd need to quote.",
  },
  {
    icon: Zap,
    title: "It lands with you",
    body: "By text, email, or a webhook into your CRM, the moment they hit submit. No portal to log into, no daily digest.",
  },
];

const FAQ = [
  {
    q: "Are the leads shared with other companies?",
    a: "No. Each request goes to one licensed provider. We don't send the same homeowner to a list of companies and let everyone race to the phone.",
  },
  {
    q: "How do leads get to me?",
    a: "Your choice of text message, email, or a webhook into your CRM. You can use more than one. Leads are sent the moment the homeowner submits.",
  },
  {
    q: "Do I need to be licensed?",
    a: "Yes. We tell homeowners we only connect them with licensed Connecticut companies, so we ask for your CT DEEP pesticide license number when you apply.",
  },
  {
    q: "Does DuckTheBugs do pest control?",
    a: "No. DuckTheBugs is a Connecticut pest education site and matching service. We don't spray, treat or inspect anything, and we don't compete with you for the job.",
  },
  {
    q: "What does it cost?",
    a: "We'll go over pricing with you on a short call before any lead is sent.",
  },
];

function Chip({ active, children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${
        active
          ? "bg-terracotta/10 border-terracotta text-forest-950 ring-1 ring-terracotta"
          : "bg-card border-forest/15 text-forest-950/75 hover:border-forest/35"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function SampleLead() {
  // Illustrative only: the shape of a lead, with an invented homeowner.
  return (
    <div className="rounded-3xl bg-cream text-forest-950 shadow-2xl shadow-black/30 overflow-hidden" data-testid="pros-sample-lead">
      <div className="flex items-center justify-between px-5 py-4 bg-forest-950/[0.04] border-b border-forest/10">
        <div className="flex items-center gap-2.5">
          <DuckBadge size={32} ring={false} />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">Sample lead</p>
            <p className="font-serif font-bold leading-tight">Ticks · Madison 06443</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-950 text-cream text-[11px] font-semibold px-3 py-1.5">
          <Lock className="w-3 h-3" /> Exclusive
        </span>
      </div>
      <dl className="px-5 py-4 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2.5 text-sm">
        <dt className="text-forest-950/55">How soon</dt>
        <dd className="font-medium">Within a few days</dd>
        <dt className="text-forest-950/55">Property</dt>
        <dd className="font-medium">Single-family home</dd>
        <dt className="text-forest-950/55">Name</dt>
        <dd className="font-medium">Dana R.</dd>
        <dt className="text-forest-950/55">Prefers</dt>
        <dd className="font-medium">Text message</dd>
      </dl>
      <div className="mx-5 mb-5 rounded-2xl bg-pill/70 px-4 py-3 text-sm text-forest-950/80 leading-relaxed">
        "Pulled two ticks off the dog this week. Yard backs onto woods."
      </div>
    </div>
  );
}

function ApplyForm() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    license_number: "",
    website: "",
    pests: [],
    counties: [],
    delivery: [],
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email || !form.phone) {
      toast.error("Please add your company, name, email and phone.");
      return;
    }
    if (!form.license_number) {
      toast.error("We need your CT DEEP pesticide license number.");
      return;
    }
    if (form.pests.length === 0 || form.counties.length === 0) {
      toast.error("Pick at least one pest and one county.");
      return;
    }
    setBusy(true);
    try {
      await submitProApplication({
        ...form,
        pests: form.pests.join(", "),
        counties: form.counties.join(", "),
        delivery: form.delivery.join(", "),
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-[2rem] border border-forest/10 bg-cream shadow-xl shadow-forest/5 p-8 sm:p-12 text-center"
        data-testid="pros-thank-you"
      >
        <div className="flex justify-center mb-5">
          <div className="relative">
            <DuckBadge size={80} />
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-duckgreen flex items-center justify-center ring-4 ring-cream">
              <CheckCircle2 className="w-5 h-5 text-forest-950" />
            </div>
          </div>
        </div>
        <h3 className="font-serif text-3xl font-bold text-forest-950">Application received.</h3>
        <p className="mt-3 text-forest-950/75 max-w-md mx-auto leading-relaxed">
          We'll check your license, then reach out to set up a short call about pricing and where you want leads.
        </p>
      </motion.div>
    );
  }

  const inputCls =
    "w-full px-4 py-3.5 rounded-2xl border border-forest/12 bg-card text-forest-950 placeholder:text-forest-950/40 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none transition-colors";
  const labelCls = "block text-sm font-medium text-forest-950 mb-1.5";
  const groupCls = "font-serif text-lg font-bold text-forest-950 mb-3";

  return (
    <form
      onSubmit={submit}
      className="rounded-[2rem] border border-forest/10 bg-cream shadow-xl shadow-forest/5 overflow-hidden"
      data-testid="pros-apply-form"
    >
      <div className="bg-forest-950 px-6 sm:px-10 py-6 flex items-center gap-3">
        <DuckBadge size={44} />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-duckgreen">For licensed CT pros</p>
          <p className="text-cream font-serif text-lg font-bold">Apply for exclusive leads</p>
        </div>
      </div>

      <div className="px-6 sm:px-10 py-8 space-y-7">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="pro-company">Company name</label>
            <input id="pro-company" autoComplete="organization" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="pros-company" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pro-name">Your name</label>
            <input id="pro-name" autoComplete="name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} data-testid="pros-name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pro-email">Email</label>
            <input id="pro-email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="pros-email" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pro-phone">Phone</label>
            <input id="pro-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="pros-phone" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pro-license">CT DEEP pesticide license #</label>
            <input id="pro-license" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} className={inputCls} data-testid="pros-license" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pro-site">Website <span className="text-forest-950/40 font-normal">(optional)</span></label>
            <input id="pro-site" type="url" inputMode="url" placeholder="https://" value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} data-testid="pros-website" />
          </div>
        </div>

        <div>
          <h3 className={groupCls}>Which pests do you treat?</h3>
          <div className="flex flex-wrap gap-2.5">
            {PEST_CHOICES.map((p) => (
              <Chip key={p} active={form.pests.includes(p)} onClick={() => toggle("pests", p)} testid={`pros-pest-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {p}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <h3 className={groupCls}>Which counties do you serve?</h3>
          <div className="flex flex-wrap gap-2.5">
            {CT_COUNTIES.map((c) => (
              <Chip key={c} active={form.counties.includes(c)} onClick={() => toggle("counties", c)} testid={`pros-county-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                {c}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <h3 className={groupCls}>How do you want leads delivered?</h3>
          <div className="flex flex-wrap gap-2.5">
            {DELIVERY.map((d) => (
              <Chip key={d} active={form.delivery.includes(d)} onClick={() => toggle("delivery", d)} testid={`pros-delivery-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {d}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls} htmlFor="pro-notes">Anything else? <span className="text-forest-950/40 font-normal">(optional)</span></label>
          <textarea id="pro-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Towns you focus on, jobs you don't take, how many leads you can handle a week…" className={inputCls} data-testid="pros-notes" />
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta/90 transition-all active:scale-95 disabled:opacity-60"
          data-testid="pros-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {busy ? "Sending…" : "Apply for leads"}
        </button>
      </div>
    </form>
  );
}

export default function Pros() {
  return (
    <div data-testid="pros-page">
      <Seo
        title="Exclusive Pest Control Leads in Connecticut"
        description="For licensed Connecticut pest control companies. Exclusive leads from homeowners who read our CT pest guides first, delivered by text, email or webhook the moment they submit."
        path="/pros"
      />

      {/* Hero */}
      <section className="bg-forest-950 text-cream grain pt-[72px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-duckgreen">
              For licensed Connecticut pest control pros
            </span>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.03]">
              Exclusive leads from homeowners who <span className="text-terracotta">did their homework.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-cream/80 max-w-xl leading-relaxed">
              Connecticut homeowners read our pest guides, then ask for a pro. When they do, the request comes to you
              with the details you need to quote, and to no one else.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#apply"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta/90 transition-all active:scale-95"
                data-testid="pros-hero-cta"
              >
                Apply for leads <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#whats-in-a-lead"
                className="inline-flex items-center justify-center px-7 py-4 rounded-full border border-cream/25 text-cream font-semibold hover:bg-cream/10 transition-all"
              >
                See what's in a lead
              </a>
            </div>
          </div>
          <Reveal delay={0.15}>
            <SampleLead />
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
        <Reveal>
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-terracotta">How it works</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-forest-950 max-w-2xl">
            Education first. The request comes after.
          </h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-forest/10 bg-card p-7">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-duckgreen/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-forest" />
                  </div>
                  <span className="text-sm font-semibold text-forest-950/40">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-serif text-xl font-bold text-forest-950">{s.title}</h3>
                <p className="mt-2 text-sm text-forest-950/70 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* What's in a lead */}
      <section id="whats-in-a-lead" className="bg-pill/40 border-y border-forest/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20 grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16">
          <Reveal>
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-terracotta">Every lead</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-forest-950">
              The answers to your first questions, before you call.
            </h2>
            <p className="mt-4 text-forest-950/70 leading-relaxed">
              No "need pest control, call me" with a phone number and nothing else. The homeowner has already told us
              what it is, where it is, and how soon they need you.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <dl className="grid sm:grid-cols-2 gap-3">
              {LEAD_FIELDS.map((f) => (
                <div key={f.k} className="rounded-2xl bg-cream border border-forest/10 px-5 py-4 flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-forest shrink-0 mt-0.5" />
                  <div>
                    <dt className="font-semibold text-forest-950">{f.k}</dt>
                    <dd className="mt-0.5 text-sm text-forest-950/65 leading-relaxed">{f.v}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* Exclusive + licensed */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20 grid md:grid-cols-2 gap-5">
        <Reveal>
          <div className="h-full rounded-3xl bg-forest-950 text-cream p-8 sm:p-10">
            <Lock className="w-7 h-7 text-duckgreen" />
            <h3 className="mt-5 font-serif text-2xl sm:text-3xl font-bold">One request. One provider.</h3>
            <p className="mt-3 text-cream/75 leading-relaxed">
              Each homeowner request goes to one company. You're not racing four competitors to the phone, and the
              homeowner isn't fielding five calls in ten minutes.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="h-full rounded-3xl border border-forest/10 bg-card p-8 sm:p-10">
            <ShieldCheck className="w-7 h-7 text-terracotta" />
            <h3 className="mt-5 font-serif text-2xl sm:text-3xl font-bold text-forest-950">Licensed only. Connecticut only.</h3>
            <p className="mt-3 text-forest-950/70 leading-relaxed">
              We tell homeowners we only connect them with licensed Connecticut companies, and we mean it. Every
              provider we work with holds a CT DEEP pesticide license.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Apply */}
      <section id="apply" className="scroll-mt-20 max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 sm:pb-20">
        <Reveal>
          <ApplyForm />
        </Reveal>
        <p className="mt-4 flex items-start gap-2 text-xs text-forest-950/55 leading-relaxed">
          <MessageSquare className="w-4 h-4 shrink-0 mt-px" />
          We'll review your application and get back to you. Applying doesn't commit you to anything.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 sm:pb-24">
        <Faq items={FAQ} title="Questions pros ask" />
        <p className="mt-8 text-sm text-forest-950/60">
          Homeowner looking for help instead?{" "}
          <Link to="/hire-a-pro" className="font-semibold text-forest hover:text-terracotta transition-colors">
            Find a pro here
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
